import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, type DriverProfile } from '../../lib/auth';
import { useSetPageHeader } from '../../components/app/AppShell';
import { DocPicker } from '../../components/form/DocPicker';
import { apiFetch } from '../../lib/api-client';
import { invalidateDoc } from '../../lib/doc-cache';
import { loadKycDraft, saveKycDraft, clearKycDraft } from '../../lib/kyc-draft';
import { haptic } from '../../lib/haptic';

const errorStyle: CSSProperties = {
  color: '#B91C1C',
  fontSize: 12,
  margin: 0,
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function KycDlEditRoute() {
  const { t } = useTranslation('profile');
  useSetPageHeader({ backTo: '/profile/kyc', title: t('kyc.dlTitle') }, []);
  const navigate = useNavigate();
  const driver = useAuth(s => s.driver);

  // Draft wins over the server value (#500) — a typed-but-unsaved entry survives
  // a background/return / app restart via localStorage.
  const [dlNumber, setDlNumber] = useState(() => loadKycDraft<{ dlNumber?: string }>(driver?.id, 'dl')?.dlNumber ?? driver?.drivingLicenseNumber ?? '');
  const [dlExpiry, setDlExpiry] = useState(() => loadKycDraft<{ dlExpiry?: string }>(driver?.id, 'dl')?.dlExpiry ?? driver?.drivingLicenseExpiry ?? '');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [touched, setTouched] = useState<{ dl: boolean; expiry: boolean; photo: boolean }>({ dl: false, expiry: false, photo: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = loadKycDraft<{ dlNumber?: string; dlExpiry?: string }>(driver?.id, 'dl');
    setDlNumber(d?.dlNumber ?? driver?.drivingLicenseNumber ?? '');
    setDlExpiry(d?.dlExpiry ?? driver?.drivingLicenseExpiry ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed (draft-aware) on driver identity change
  }, [driver?.id]);

  // Persist the typed values as a draft so a background/return doesn't lose them (#500).
  useEffect(() => {
    if (driver?.id && (touched.dl || touched.expiry)) saveKycDraft(driver.id, 'dl', { dlNumber, dlExpiry });
  }, [dlNumber, dlExpiry, touched.dl, touched.expiry, driver?.id]);

  const today = todayIso();
  const dlValid = dlNumber.length === 0 || (dlNumber.length >= 8 && dlNumber.length <= 16);
  const expiryValid = dlExpiry.length === 0 || dlExpiry > today;
  const canSave = dlValid && expiryValid && !busy;

  const handleSave = async () => {
    if (!driver) return;
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      if (touched.dl)     payload.drivingLicenseNumber = dlNumber;
      if (touched.expiry) payload.drivingLicenseExpiry = dlExpiry;
      if (touched.photo && photoDataUrl) payload.drivingLicenseUrl = photoDataUrl;

      try {
        await apiFetch('/driver-app/me/kyc', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } catch (e) {
        // 401 means the driver's session has expired — redirect to auth.
        // Everything else bubbles to the outer catch and renders inline.
        if ((e as { status?: number })?.status === 401) {
          navigate('/auth', { replace: true });
          return;
        }
        throw e;
      }
      const updated = await apiFetch<DriverProfile>('/driver-app/me');
      useAuth.setState({ driver: updated });
      await invalidateDoc('dl');
      clearKycDraft(driver.id, 'dl');
      haptic.success();
      navigate('/profile/kyc');
    } catch (e: unknown) {
      // Accept either Error (legacy) or ApiError shape ({ status, code, message })
      // from apiFetch.
      const errMsg = (e as { message?: string } | null | undefined)?.message;
      setError(errMsg || t('kyc.submitError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pt-2 pb-10">
      <div className="space-y-3 mb-4">
        <div>
          <label htmlFor="dl-number-input" className="block text-sm font-semibold mb-1">{t('signupFields.dlNumber', { ns: 'auth' })}</label>
          <input
            id="dl-number-input"
            type="text"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            value={dlNumber}
            onChange={(e) => { setDlNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 16)); setTouched(p => ({ ...p, dl: true })); }}
            className="w-full px-3 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] text-base"
          />
          {dlNumber.length > 0 && !dlValid ? <p style={errorStyle}>{t('signupValidation.dlInvalid', { ns: 'auth', defaultValue: 'DL number must be 8–16 characters' })}</p> : null}
        </div>

        <div>
          <label htmlFor="dl-expiry-input" className="block text-sm font-semibold mb-1">{t('signupFields.dlExpiry', { ns: 'auth' })}</label>
          <input
            id="dl-expiry-input"
            type="date"
            value={dlExpiry}
            min={today}
            onChange={(e) => { setDlExpiry(e.target.value); setTouched(p => ({ ...p, expiry: true })); }}
            className="block w-full min-w-0 px-3 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] text-base bg-transparent appearance-none [&::-webkit-date-and-time-value]:text-left"
          />
          {dlExpiry.length > 0 && !expiryValid ? <p style={errorStyle}>{t('signupValidation.dlExpired', { ns: 'auth', defaultValue: 'Expiry must be a future date' })}</p> : null}
        </div>

        <DocPicker
          value={photoDataUrl}
          onChange={(dataUrl) => { setPhotoDataUrl(dataUrl); setTouched(p => ({ ...p, photo: true })); }}
          label={driver?.hasDl ? t('kyc.photoOnFile') : t('kyc.noPhotoYet')}
          accept="image/*,application/pdf"
        />
      </div>

      {error ? <p role="alert" style={{ ...errorStyle, margin: '0 0 0.5rem' }}>{error}</p> : null}

      <button
        type="button"
        disabled={!canSave}
        onClick={() => { haptic.select(); handleSave(); }}
        className="w-full py-3 rounded-full font-bold disabled:opacity-50"
        style={{ background: 'var(--color-btn-primary)', color: '#fff', minHeight: 'var(--touch-target, 52px)' }}
      >
        {busy ? t('kyc.save') + '…' : t('kyc.save')}
      </button>
    </div>
  );
}
